'use client';
// MoreDropdown Component - Responsive: Modal on mobile, Dropdown on desktop
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import CloseIcon from '@mui/icons-material/Close';
import DashboardIcon from '@mui/icons-material/Dashboard';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import {
  alpha,
  Box,
  Dialog,
  IconButton,
  MenuList,
  Paper,
  Portal,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { MoreDropdownItem } from './MoreDropdownItem';

import { ROUTES } from '@/constants';

interface MoreDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  triggerRef: React.RefObject<HTMLElement>;
}

interface MoreItem {
  label: string;
  icon: React.ReactNode;
  path?: string;
}

const MORE_ITEMS: readonly MoreItem[] = [
  {
    label: 'Activity',
    icon: <ShowChartIcon sx={{ fontSize: '1.25rem' }} />,
    path: ROUTES.ACTIVITY,
  },
  {
    label: 'Leaderboard',
    icon: <EmojiEventsIcon sx={{ fontSize: '1.25rem' }} />,
    path: ROUTES.LEADERBOARD,
  },
  { label: 'Dashboards', icon: <DashboardIcon sx={{ fontSize: '1.25rem' }} /> },
];

const DROPDOWN_WIDTH = 220;

export const MoreDropdown = ({ isOpen, onClose, triggerRef }: MoreDropdownProps) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [position, setPosition] = useState({ top: 0, left: 0 });

  // Calculate position when dropdown opens (desktop only)
  useEffect(() => {
    if (isOpen && triggerRef.current && !isMobile) {
      const updatePosition = () => {
        if (triggerRef.current) {
          const rect = triggerRef.current.getBoundingClientRect();
          const viewportWidth = window.innerWidth;

          // Align dropdown's right edge with trigger's right edge so it opens to the left
          const desiredLeft = rect.right - DROPDOWN_WIDTH;

          // Clamp within viewport with small margin
          const minLeft = 8;
          const maxLeft = viewportWidth - DROPDOWN_WIDTH - 8;

          setPosition({
            top: rect.bottom + 8, // 8px below the trigger
            left: Math.min(maxLeft, Math.max(minLeft, desiredLeft)),
          });
        }
      };

      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isOpen, triggerRef, isMobile]);

  // Click outside handler (desktop only)
  useEffect(() => {
    if (isMobile) return; // Don't need this for modal

    const handleClickOutside = (event: MouseEvent) => {
      const dropdownEl = dropdownRef.current;
      const triggerEl = triggerRef.current;

      if (
        dropdownEl &&
        typeof dropdownEl.contains === 'function' &&
        !dropdownEl.contains(event.target as Node) &&
        triggerEl &&
        typeof triggerEl.contains === 'function' &&
        !triggerEl.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, triggerRef, isMobile]);

  const createItemClickHandler = useCallback(
    (item: MoreItem) => () => {
      if (item.path) {
        router.push(item.path);
      } else {
        // TODO: Implement navigation for items without paths
      }
      onClose();
    },
    [router, onClose]
  );

  const itemsWithHandlers = useMemo(
    () =>
      MORE_ITEMS.map((item) => ({
        ...item,
        onClick: createItemClickHandler(item),
      })),
    [createItemClickHandler]
  );

  if (!isOpen) {
    return null;
  }

  // Mobile: Show as centered modal
  if (isMobile) {
    return (
      <Dialog
        open={isOpen}
        onClose={onClose}
        fullWidth
        maxWidth="xs"
        sx={{
          '& .MuiDialog-paper': {
            m: 2,
            maxWidth: '400px',
            borderRadius: '12px',
            bgcolor: 'background.paper',
          },
          '& .MuiBackdrop-root': {
            bgcolor: 'rgba(0, 0, 0, 0.5)',
          },
        }}
        TransitionProps={{
          timeout: 250,
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
              pb: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="h6" fontWeight={600}>
              More
            </Typography>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Menu Items */}
          <MenuList sx={{ py: 0 }}>
            {itemsWithHandlers.map((item) => (
              <MoreDropdownItem
                key={item.label}
                label={item.label}
                icon={item.icon}
                onClick={item.onClick}
              />
            ))}
          </MenuList>
        </Box>
      </Dialog>
    );
  }

  // Desktop: Show as dropdown
  return (
    <Portal>
      <Paper
        ref={dropdownRef}
        elevation={8}
        sx={{
          position: 'fixed',
          top: position.top,
          left: position.left,
          width: DROPDOWN_WIDTH,
          zIndex: 10001,
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: (theme) =>
            theme.palette.mode === 'dark'
              ? `0 8px 24px ${alpha(theme.palette.common.black, 0.5)}`
              : `0 8px 24px ${alpha(theme.palette.common.black, 0.12)}`,
          animation: 'slideIn 0.2s ease-out',
          '@keyframes slideIn': {
            from: { opacity: 0, transform: 'translateY(-8px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
          },
        }}
      >
        <MenuList sx={{ py: 0.5 }}>
          {itemsWithHandlers.map((item) => (
            <MoreDropdownItem
              key={item.label}
              label={item.label}
              icon={item.icon}
              onClick={item.onClick}
            />
          ))}
        </MenuList>
      </Paper>
    </Portal>
  );
};

export default MoreDropdown;
